/**
 * Mobile sticky buy bar.
 *
 * The product form's own Add to cart and Buy now stay inline in the page,
 * below the colour swatches. This bar is a proxy pinned to the bottom of the
 * screen: it shows while those inline buttons are off-screen, and hides as
 * soon as they scroll into view, so the two are never on screen together.
 *
 * Its buttons do not submit anything themselves. They forward the tap to the
 * real controls, which keeps one source of truth for the variant, the stock
 * state and the payment gateway. That matters for Buy now in particular:
 * Shopify renders one dynamic checkout button per form and Razorpay's Magic
 * button lives in a closed shadow root, so neither can be duplicated - it can
 * only be driven.
 */
(() => {
  const MOBILE = '(max-width: 767px)';

  const init = () => {
    const bar = document.querySelector('.sticky-buy-bar');
    const section = document.querySelector('.section-main-product');
    if (!bar || !section) return;

    const row = section.querySelector('.product-form .quantity-submit-row');
    if (!row || row.closest('.quickbuy-container')) return;

    const realAtc = row.querySelector('button.add-to-cart');
    const paymentHost = row.querySelector('.product-dynamic-payment-button');

    const proxyAtc = bar.querySelector('[data-sticky-atc]');
    const proxyBuy = bar.querySelector('[data-sticky-buy]');

    /* ---------- visibility ---------- */

    const mq = window.matchMedia(MOBILE);
    let observer = null;

    const show = (visible) => bar.classList.toggle('sticky-buy-bar--in', visible);

    const startWatching = () => {
      if (observer) return;
      observer = new IntersectionObserver(
        (entries) => show(!entries[0].isIntersecting),
        // A sliver of the row counts as "reached", so the bar gets out of the
        // way before it can overlap the real buttons.
        { threshold: 0, rootMargin: '0px 0px -80px 0px' }
      );
      observer.observe(row);
    };

    const stopWatching = () => {
      if (!observer) return;
      observer.disconnect();
      observer = null;
      show(false);
    };

    const sync = () => (mq.matches ? startWatching() : stopWatching());

    sync();
    mq.addEventListener('change', sync);

    /* ---------- keep the proxy in step with the real button ---------- */

    const syncAtcState = () => {
      if (!realAtc || !proxyAtc) return;
      proxyAtc.disabled = realAtc.disabled;
      proxyAtc.textContent = realAtc.textContent.trim();
    };

    syncAtcState();

    // The theme re-renders the buy row on variant change, which swaps the
    // button element, so watch the row rather than the button.
    new MutationObserver(syncAtcState).observe(row, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });

    /* ---------- forwarding ---------- */

    const realPaymentButton = () =>
      paymentHost &&
      paymentHost.querySelector(
        '.shopify-payment-button__button, .rzp-magic-btn__buy-product, .magic-buy-btn button, .magic-buy-btn'
      );

    proxyAtc?.addEventListener('click', () => {
      section.querySelector('.product-form .quantity-submit-row button.add-to-cart')?.click();
    });

    proxyBuy?.addEventListener('click', () => {
      const target = realPaymentButton();

      if (target) {
        target.click();
        return;
      }

      // Razorpay's button can refuse a synthetic click from inside its closed
      // shadow root. Falling back to scrolling the real one into view keeps
      // the tap meaningful instead of doing nothing at all.
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
